import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Eye, EyeOff, Mail, Lock, User, ShoppingBag, Car, 
  Hotel, Plane, Utensils, ShoppingCart, ChevronRight, Check,
  Building2, UserCircle, Upload, FileText, Globe, MapPin, Phone,
  Calendar, Flag, CreditCard, AlertCircle, Loader2, Wrench, CircleDot, Store, CarTaxiFront, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { LanguageSelector } from '@/components/LanguageSelector';
import { buildPathForLocale } from '@/lib/intl-seo';

// All countries list
const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahrain", "Bangladesh", "Belarus", "Belgium", "Bolivia", "Bosnia and Herzegovina",
  "Brazil", "Bulgaria", "Cambodia", "Cameroon", "Canada", "Chile", "China", "Colombia", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Ecuador", "Egypt", "Estonia", "Ethiopia",
  "Finland", "France", "Georgia", "Germany", "Ghana", "Greece", "Guatemala", "Hong Kong", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kosovo", "Kuwait", "Kyrgyzstan", "Latvia", "Lebanon", "Libya", "Lithuania",
  "Luxembourg", "Macedonia", "Malaysia", "Malta", "Mexico", "Moldova", "Monaco", "Mongolia", "Montenegro",
  "Morocco", "Nepal", "Netherlands", "New Zealand", "Nigeria", "Norway", "Oman", "Pakistan", "Palestine",
  "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia",
  "Saudi Arabia", "Serbia", "Singapore", "Slovakia", "Slovenia", "South Africa", "South Korea", "Spain",
  "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand",
  "Tunisia", "Turkey", "Türkiye", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Venezuela", "Vietnam", "Yemen"
].sort();

const sellerCategories = [
  { id: 'supermarket', icon: ShoppingCart, color: 'bg-teal-500', borderColor: 'border-teal-500', lightBg: 'bg-teal-50', textColor: 'text-teal-600' },
  { id: 'products', icon: ShoppingBag, color: 'bg-blue-500', borderColor: 'border-blue-500', lightBg: 'bg-blue-50', textColor: 'text-blue-600' },
  { id: 'restaurants', icon: Utensils, color: 'bg-red-500', borderColor: 'border-red-500', lightBg: 'bg-red-50', textColor: 'text-red-600' },
  { id: 'cars', icon: Car, color: 'bg-green-500', borderColor: 'border-green-500', lightBg: 'bg-green-50', textColor: 'text-green-600' },
  { id: 'auto-parts', icon: Wrench, color: 'bg-slate-600', borderColor: 'border-slate-600', lightBg: 'bg-slate-50', textColor: 'text-slate-600' },
  { id: 'tires', icon: CircleDot, color: 'bg-gray-700', borderColor: 'border-gray-700', lightBg: 'bg-gray-50', textColor: 'text-gray-700' },
  { id: 'hotels', icon: Hotel, color: 'bg-purple-500', borderColor: 'border-purple-500', lightBg: 'bg-purple-50', textColor: 'text-purple-600' },
  { id: 'flights', icon: Plane, color: 'bg-orange-500', borderColor: 'border-orange-500', lightBg: 'bg-orange-50', textColor: 'text-orange-600' },
  { id: 'taxi-limousine', icon: CarTaxiFront, color: 'bg-yellow-500', borderColor: 'border-yellow-500', lightBg: 'bg-yellow-50', textColor: 'text-yellow-600' }
];

const SellerRegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || '';
  const { executeRecaptcha } = useGoogleReCaptcha();
  const currentLang = i18n.language || 'tr';
  const lc = currentLang.substring(0, 2);
  const homePath = buildPathForLocale(lc, []);
  const pendingPath = buildPathForLocale(lc, ['seller-registration-pending']);
  const loginPath = buildPathForLocale(lc, ['login']);

  // Step tracking
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Category selection
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  
  // Step 2: Seller type selection
  const [sellerType, setSellerType] = useState(''); // individual, company
  
  // Step 3: Account info
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docWarnings, setDocWarnings] = useState({ id: '', trade: '', auth: '' });
  
  // Form data
  const [form, setForm] = useState({
    // Basic account
    email: '',
    password: '',
    storeName: '',
    phone: '',
    
    // Address
    country: '',
    city: '',
    postalCode: '',
    fullAddress: '',
    
    // Individual fields
    firstName: '',
    lastName: '',
    birthDate: '',
    nationality: '',
    taxNumber: '',
    idDocumentUrl: '',
    
    // Company fields
    companyName: '',
    companyTradeRegister: '',
    authorizedPersonName: '',
    companyTaxNumber: '',
    tradeRegisterDocUrl: '',
    authorizedPersonIdUrl: '',
    
    // Restaurant claim fields
    selectedRestaurantId: '',
    selectedRestaurantName: ''
  });

  // Restaurant search states
  const [restaurantSearchQuery, setRestaurantSearchQuery] = useState('');
  const [restaurantSearchResults, setRestaurantSearchResults] = useState([]);
  const [searchingRestaurants, setSearchingRestaurants] = useState(false);

  const idDocInputRef = useRef(null);
  const tradeDocInputRef = useRef(null);
  const authorizedIdInputRef = useRef(null);

  // File upload handlers
  const ALLOWED_DOC_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];
  const ALLOWED_DOC_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];
  const DOC_ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,application/pdf';

  const validateFile = (file, field) => {
    const name = (file.name || '').toLowerCase();
    const extOk = ALLOWED_DOC_EXTENSIONS.some((ext) => name.endsWith(ext));
    const typeOk = !file.type || ALLOWED_DOC_TYPES.includes(file.type);
    const warn = (message) => {
      if (field) setDocWarnings((prev) => ({ ...prev, [field]: message }));
      toast.error(message, { duration: 6000 });
    };
    if (!extOk || !typeOk) {
      warn(t('seller.register.invalidFileTypeDoc', {
        fileName: file.name,
        defaultValue: '"{{fileName}}" yüklenemedi. Sadece JPG, PNG, WebP veya PDF dosyaları kabul edilmektedir.',
      }));
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      warn(t('seller.register.fileTooLargeDoc', 'Dosya boyutu 10MB\'dan küçük olmalıdır.'));
      return false;
    }
    if (field) setDocWarnings((prev) => ({ ...prev, [field]: '' }));
    return true;
  };

  const handleFileUpload = async (file, docType, field) => {
    if (!file) return null;
    if (!validateFile(file, field)) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      // Do not set Content-Type manually — api interceptor clears it for FormData
      // so the browser can send multipart boundary.
      const response = await api.post(`/auth/upload-document?doc_type=${encodeURIComponent(docType)}`, formData);
      const url = response?.data?.url;
      if (!url) {
        toast.error(t('seller.register.fileUploadFailed'));
        return null;
      }
      toast.success(t('seller.register.fileUploaded'));
      return url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map((d) => d.msg || d).join(', ') : null);
      toast.error(msg || t('seller.register.fileUploadFailed'));
      console.error('Document upload failed:', error.response?.status, error.response?.data || error);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleIdDocumentChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await handleFileUpload(file, 'id_document', 'id');
      if (url) setForm(prev => ({ ...prev, idDocumentUrl: url }));
    }
    e.target.value = '';
  };

  const handleTradeRegisterDocChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await handleFileUpload(file, 'trade_register', 'trade');
      if (url) setForm(prev => ({ ...prev, tradeRegisterDocUrl: url }));
    }
    e.target.value = '';
  };

  const handleAuthorizedIdChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = await handleFileUpload(file, 'authorized_id', 'auth');
      if (url) setForm(prev => ({ ...prev, authorizedPersonIdUrl: url }));
    }
    e.target.value = '';
  };

  // Search restaurants for claim
  const searchRestaurants = async (query) => {
    if (!query || query.length < 2) {
      setRestaurantSearchResults([]);
      return;
    }
    
    setSearchingRestaurants(true);
    try {
      const response = await api.get(`/restaurants?search=${encodeURIComponent(query)}&limit=10&status=pending_claim`);
      setRestaurantSearchResults(response.data.restaurants || []);
    } catch (error) {
      console.error('Restaurant search error:', error);
      setRestaurantSearchResults([]);
    } finally {
      setSearchingRestaurants(false);
    }
  };

  // Debounced restaurant search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (restaurantSearchQuery) {
        searchRestaurants(restaurantSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [restaurantSearchQuery]);

  const selectRestaurant = (restaurant) => {
    setForm(prev => ({
      ...prev,
      selectedRestaurantId: restaurant.id,
      selectedRestaurantName: restaurant.name,
      storeName: restaurant.name,
      country: restaurant.country,
      city: restaurant.city,
      fullAddress: restaurant.address || ''
    }));
    setRestaurantSearchQuery('');
    setRestaurantSearchResults([]);
  };

  const validateStep3 = () => {
    console.log('=== Validating Step 3 ===');
    console.log('form:', JSON.stringify(form, null, 2));
    console.log('sellerType:', sellerType);
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email || !emailRegex.test(form.email)) {
      console.log('VALIDATION FAILED: Invalid email');
      toast.error(t('validation.invalidEmail'));
      return false;
    }
    
    // Password validation - must have 8+ chars, uppercase, lowercase, and number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!form.password || !passwordRegex.test(form.password)) {
      console.log('VALIDATION FAILED: Password does not meet requirements');
      toast.error(t('auth.passwordRequirements'));
      return false;
    }
    
    if (!form.storeName || form.storeName.length < 2) {
      console.log('VALIDATION FAILED: Store name missing');
      toast.error(t('seller.register.storeNameRequired'));
      return false;
    }
    
    if (!form.phone || form.phone.length < 10) {
      console.log('VALIDATION FAILED: Phone invalid');
      toast.error(t('validation.invalidPhone'));
      return false;
    }
    
    if (!form.country || !form.city || !form.fullAddress) {
      console.log('VALIDATION FAILED: Address fields missing', {country: form.country, city: form.city, fullAddress: form.fullAddress});
      toast.error(t('seller.register.fillAddressFields'));
      return false;
    }
    
    if (sellerType === 'individual') {
      if (!form.firstName || !form.lastName || !form.taxNumber) {
        console.log('VALIDATION FAILED: Individual fields missing');
        toast.error(t('seller.register.fillIndividualFields'));
        return false;
      }
      if (!form.idDocumentUrl) {
        console.log('VALIDATION FAILED: ID document missing');
        toast.error(t('seller.register.uploadIdDocument'));
        return false;
      }
    } else if (sellerType === 'company') {
      if (!form.companyName || !form.companyTaxNumber || !form.companyTradeRegister) {
        console.log('VALIDATION FAILED: Company fields missing');
        toast.error(t('seller.register.fillCompanyFields'));
        return false;
      }
      if (!form.tradeRegisterDocUrl) {
        console.log('VALIDATION FAILED: Trade register doc missing');
        toast.error(t('seller.register.uploadTradeRegister'));
        return false;
      }
      if (!form.authorizedPersonIdUrl) {
        console.log('VALIDATION FAILED: Authorized person ID missing');
        toast.error(t('seller.register.uploadAuthorizedId'));
        return false;
      }
    }
    
    console.log('VALIDATION PASSED');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== Form Submit Started ===');
    console.log('sellerType:', sellerType);
    console.log('form data:', form);
    
    if (!validateStep3()) {
      console.log('Validation failed at step 3');
      return;
    }
    console.log('Validation passed');
    
    // For restaurant claim, require restaurant selection
    if (sellerType === 'restaurant_claim' && !form.selectedRestaurantId) {
      toast.error(t('seller.register.selectRestaurantFirst'));
      return;
    }
    
    setLoading(true);
    console.log('Loading set to true');

    try {
      let recaptchaToken = null;
      console.log('executeRecaptcha available:', !!executeRecaptcha);
      
      if (executeRecaptcha) {
        try {
          console.log('Attempting reCAPTCHA...');
          recaptchaToken = await executeRecaptcha('register');
          console.log('reCAPTCHA token received:', !!recaptchaToken);
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          toast.error(t('auth.recaptchaFailed'));
          setLoading(false);
          return;
        }
      } else {
        console.log('No executeRecaptcha function available - proceeding without reCAPTCHA');
      }
      
      console.log('Calling register API...');
      // Register basic account
      await register({ 
        email: form.email,
        password: form.password,
        name: form.storeName,
        role: 'seller',
        seller_category: selectedCategory,
        seller_type: sellerType,
        restaurant_claim_id: sellerType === 'restaurant_claim' ? form.selectedRestaurantId : null,
        recaptcha_token: recaptchaToken,
        // Individual seller fields
        first_name: form.firstName || null,
        last_name: form.lastName || null,
        tax_number: form.taxNumber || null,
        id_document_url: form.idDocumentUrl || null,
        phone: form.phone || null,
        // Company seller fields
        company_name: form.companyName || null,
        company_tax_number: form.companyTaxNumber || null,
        company_trade_register: form.companyTradeRegister || null,
        trade_register_doc_url: form.tradeRegisterDocUrl || null,
        authorized_person_name: form.authorizedPersonName || null,
        authorized_person_id_url: form.authorizedPersonIdUrl || null,
        company_address: form.companyAddress || null,
        country: form.country || null,
        city: form.city || null,
        postal_code: form.postalCode || null,
        full_address: form.fullAddress || null,
      });
      
      // Navigate to success page with verification pending message
      navigate(pendingPath, { 
        state: { 
          email: form.email, 
          sellerType,
          category: selectedCategory,
          restaurantName: sellerType === 'restaurant_claim' ? form.selectedRestaurantName : null
        } 
      });
      
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = t('seller.register.registrationError');
      
      if (error.response?.data?.detail) {
        // Handle detail as string or object
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      console.log('Final error message:', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedCategoryData = sellerCategories.find(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
      {/* Header */}
      <div className="container mx-auto px-4 py-6 flex items-center justify-between">
        <Link to={homePath} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <img src="/logo.png" alt="Turkish E-Market" className="h-8 w-auto" />
          <span className="font-semibold text-xl text-primary">Turkish E-Market</span>
        </Link>
        <LanguageSelector variant="compact" />
      </div>

      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              {t('seller.register.title')}
            </h1>
            <p className="text-muted-foreground text-lg">
              {t('seller.register.subtitle')}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                  currentStep >= step 
                    ? (currentStep > step ? 'bg-green-500 text-white' : 'bg-primary text-white')
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step ? <Check className="w-5 h-5" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${currentStep > step ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Category Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">1</div>
                <h2 className="text-xl font-semibold">{t('seller.register.whatToSell')}</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {sellerCategories.map((category) => {
                  const Icon = category.icon;
                  const isSelected = selectedCategory === category.id;
                  
                  return (
                    <Card 
                      key={category.id}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                        isSelected 
                          ? `ring-2 ring-offset-2 ${category.borderColor} ${category.lightBg}` 
                          : 'hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedCategory(category.id)}
                      data-testid={`category-${category.id}`}
                    >
                      <CardContent className="p-4 flex flex-col items-center text-center">
                        <div className={`w-14 h-14 rounded-xl ${category.color} flex items-center justify-center mb-3 ${isSelected ? 'scale-110' : ''} transition-transform`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <h3 className={`font-semibold ${isSelected ? category.textColor : ''}`}>
                          {t(`seller.register.category.${category.id}`)}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t(`seller.register.categoryDesc.${category.id}`)}
                        </p>
                        {isSelected && (
                          <div className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${category.color} text-white`}>
                            {t('seller.register.selected')}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => setCurrentStep(2)} 
                  disabled={!selectedCategory}
                  className="rounded-full px-8"
                >
                  {t('common.continue')}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Seller Type Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">2</div>
                <h2 className="text-xl font-semibold">{t('seller.register.sellerType')}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Individual */}
                <Card 
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    sellerType === 'individual' ? 'ring-2 ring-primary bg-primary/5' : ''
                  }`}
                  onClick={() => setSellerType('individual')}
                  data-testid="seller-type-individual"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${sellerType === 'individual' ? 'bg-primary' : 'bg-gray-100'}`}>
                        <UserCircle className={`w-8 h-8 ${sellerType === 'individual' ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">{t('seller.register.individual')}</h3>
                        <p className="text-sm text-muted-foreground">{t('seller.register.individualDesc')}</p>
                      </div>
                    </div>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.individualReq1')}</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.individualReq2')}</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.individualReq3')}</li>
                    </ul>
                    {sellerType === 'individual' && (
                      <div className="mt-4 px-4 py-2 bg-primary text-white rounded-full text-center text-sm font-medium">
                        {t('seller.register.selected')}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Company */}
                <Card 
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    sellerType === 'company' ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setSellerType('company')}
                  data-testid="seller-type-company"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${sellerType === 'company' ? 'bg-blue-500' : 'bg-gray-100'}`}>
                        <Building2 className={`w-8 h-8 ${sellerType === 'company' ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">{t('seller.register.company')}</h3>
                        <p className="text-sm text-muted-foreground">{t('seller.register.companyDesc')}</p>
                      </div>
                    </div>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.companyReq1')}</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.companyReq2')}</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.companyReq3')}</li>
                    </ul>
                    {sellerType === 'company' && (
                      <div className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-full text-center text-sm font-medium">
                        {t('seller.register.selected')}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Restaurant Claim - Only visible for restaurants category */}
                {selectedCategory === 'restaurants' && (
                  <Card 
                    className={`cursor-pointer transition-all duration-200 hover:shadow-lg md:col-span-2 ${
                      sellerType === 'restaurant_claim' ? 'ring-2 ring-red-500 bg-red-50' : ''
                    }`}
                    onClick={() => setSellerType('restaurant_claim')}
                    data-testid="seller-type-restaurant-claim"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${sellerType === 'restaurant_claim' ? 'bg-red-500' : 'bg-gray-100'}`}>
                          <Utensils className={`w-8 h-8 ${sellerType === 'restaurant_claim' ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">{t('seller.register.restaurantClaim')}</h3>
                          <p className="text-sm text-muted-foreground">{t('seller.register.restaurantClaimDesc')}</p>
                        </div>
                      </div>
                      <ul className="text-sm text-muted-foreground space-y-2">
                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.restaurantClaimReq1')}</li>
                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.restaurantClaimReq2')}</li>
                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> {t('seller.register.restaurantClaimReq3')}</li>
                      </ul>
                      {sellerType === 'restaurant_claim' && (
                        <div className="mt-4 px-4 py-2 bg-red-500 text-white rounded-full text-center text-sm font-medium">
                          {t('seller.register.selected')}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="rounded-full px-8">
                  {t('common.back')}
                </Button>
                <Button 
                  onClick={() => setCurrentStep(3)} 
                  disabled={!sellerType}
                  className="rounded-full px-8"
                >
                  {t('common.continue')}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Registration Form */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">3</div>
                <h2 className="text-xl font-semibold">{t('seller.register.accountInfo')}</h2>
              </div>

              {/* Category & Type badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                {selectedCategoryData && (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${selectedCategoryData.color} text-white`}>
                    {React.createElement(selectedCategoryData.icon, { className: "w-5 h-5" })}
                    <span className="font-medium">{t(`seller.register.category.${selectedCategory}`)}</span>
                  </div>
                )}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                  sellerType === 'individual' ? 'bg-primary' : 
                  sellerType === 'company' ? 'bg-blue-500' : 
                  'bg-red-500'
                } text-white`}>
                  {sellerType === 'individual' ? <UserCircle className="w-5 h-5" /> : 
                   sellerType === 'company' ? <Building2 className="w-5 h-5" /> :
                   <Utensils className="w-5 h-5" />}
                  <span className="font-medium">
                    {sellerType === 'restaurant_claim' 
                      ? t('seller.register.restaurantClaim')
                      : t(`seller.register.${sellerType}`)}
                  </span>
                </div>
              </div>

              {/* Restaurant Claim - Search Section */}
              {sellerType === 'restaurant_claim' && !form.selectedRestaurantId && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                      <Search className="w-5 h-5" />
                      {t('seller.register.findYourRestaurant')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {t('seller.register.searchRestaurantDesc')}
                    </p>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        value={restaurantSearchQuery}
                        onChange={(e) => setRestaurantSearchQuery(e.target.value)}
                        placeholder={t('seller.register.searchRestaurantPlaceholder')}
                        className="pl-10"
                      />
                      {searchingRestaurants && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Search Results */}
                    {restaurantSearchResults.length > 0 && (
                      <div className="border rounded-lg divide-y max-h-64 overflow-auto bg-white">
                        {restaurantSearchResults.map((restaurant) => (
                          <div
                            key={restaurant.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                            onClick={() => selectRestaurant(restaurant)}
                          >
                            <div>
                              <p className="font-medium">{restaurant.name}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {restaurant.city}, {restaurant.country}
                              </p>
                            </div>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                              {t('common.select')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {restaurantSearchQuery.length >= 2 && restaurantSearchResults.length === 0 && !searchingRestaurants && (
                      <div className="text-center py-4 text-muted-foreground">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{t('seller.register.noRestaurantFound')}</p>
                        <p className="text-sm mt-1">{t('seller.register.tryDifferentSearch')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Selected Restaurant Info */}
              {sellerType === 'restaurant_claim' && form.selectedRestaurantId && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center">
                          <Utensils className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-800">{form.selectedRestaurantName}</p>
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {form.city}, {form.country}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setForm(prev => ({ 
                          ...prev, 
                          selectedRestaurantId: '', 
                          selectedRestaurantName: '',
                          storeName: '',
                          country: '',
                          city: '',
                          fullAddress: ''
                        }))}
                      >
                        {t('common.change')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <form onSubmit={handleSubmit} className="space-y-8" onKeyDown={(e) => {
                // Prevent form submission on Enter key except from submit button
                if (e.key === 'Enter' && e.target.type !== 'submit') {
                  e.preventDefault();
                }
              }}>
                {/* Basic Account Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Store className="w-5 h-5" />
                      {t('seller.register.basicInfo')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('seller.register.storeName')} *</Label>
                        <Input
                          value={form.storeName}
                          onChange={(e) => setForm({ ...form, storeName: e.target.value })}
                          placeholder={t('seller.register.storeNamePlaceholder')}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auth.email')} *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                            placeholder="seller@example.com"
                            className="pl-10"
                            required
                            autoComplete="email"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('auth.password')} *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            placeholder="••••••••"
                            className="pl-10 pr-10"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {/* Password requirements indicator */}
                        <div className="text-xs space-y-1 mt-1">
                          <p className={`flex items-center gap-1 ${form.password.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {form.password.length >= 8 ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                            {t('seller.register.passwordRuleLength')}
                          </p>
                          <p className={`flex items-center gap-1 ${/[A-Z]/.test(form.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {/[A-Z]/.test(form.password) ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                            {t('seller.register.passwordRuleUppercase')}
                          </p>
                          <p className={`flex items-center gap-1 ${/[a-z]/.test(form.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {/[a-z]/.test(form.password) ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                            {t('seller.register.passwordRuleLowercase')}
                          </p>
                          <p className={`flex items-center gap-1 ${/[0-9]/.test(form.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {/[0-9]/.test(form.password) ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                            {t('seller.register.passwordRuleDigit')}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('seller.register.phone')} *</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="+90 5XX XXX XX XX"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Address Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      {t('seller.register.addressInfo')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('seller.register.country')} *</Label>
                        <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('seller.register.selectCountry')} />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {countries.map((country) => (
                              <SelectItem key={country} value={country}>{country}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('seller.register.city')} *</Label>
                        <Input
                          value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          placeholder={t('seller.register.cityPlaceholder')}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('seller.register.postalCode')}</Label>
                        <Input
                          value={form.postalCode}
                          onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                          placeholder="34000"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('seller.register.fullAddress')} *</Label>
                      <Textarea
                        value={form.fullAddress}
                        onChange={(e) => setForm({ ...form, fullAddress: e.target.value })}
                        placeholder={t('seller.register.fullAddressPlaceholder')}
                        rows={3}
                        required
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Individual Fields */}
                {sellerType === 'individual' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <UserCircle className="w-5 h-5" />
                        {t('seller.register.personalInfo')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('seller.register.firstName')} *</Label>
                          <Input
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller.register.lastName')} *</Label>
                          <Input
                            value={form.lastName}
                            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('seller.register.birthDate')}</Label>
                          <Input
                            type="date"
                            value={form.birthDate}
                            onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller.register.nationality')}</Label>
                          <Select value={form.nationality} onValueChange={(v) => setForm({ ...form, nationality: v })}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('seller.register.selectNationality')} />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {countries.map((country) => (
                                <SelectItem key={country} value={country}>{country}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('seller.register.taxNumber')} *</Label>
                        <div className="relative">
                          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            value={form.taxNumber}
                            onChange={(e) => setForm({ ...form, taxNumber: e.target.value })}
                            placeholder={t('seller.register.taxNumberPlaceholder')}
                            className="pl-10"
                            required
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">{t('seller.register.taxNumberInfo')}</p>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('seller.register.idDocument')} *</Label>
                        <div className={`border-2 border-dashed rounded-lg p-6 text-center ${form.idDocumentUrl ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
                          {form.idDocumentUrl ? (
                            <div className="flex items-center justify-center gap-2 text-green-600">
                              <Check className="w-5 h-5" />
                              <span>{t('seller.register.documentUploaded')}</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">{t('seller.register.uploadIdDocDesc')}</p>
                            </>
                          )}
                          <input
                            ref={idDocInputRef}
                            type="file"
                            accept={DOC_ACCEPT}
                            onChange={handleIdDocumentChange}
                            className="hidden"
                            id="id-document"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-4"
                            onClick={() => idDocInputRef.current?.click()}
                            disabled={uploading}
                          >
                            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                            {form.idDocumentUrl ? t('seller.register.changeFile') : t('seller.register.selectFile')}
                          </Button>
                          <p className="text-xs text-muted-foreground mt-3">{t('seller.register.acceptedFileHint', 'Kabul edilen dosyalar: JPG, PNG, WebP veya PDF. En fazla 10 MB.')}</p>
                          {docWarnings.id ? (
                            <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-left" role="alert">
                              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-red-700">{docWarnings.id}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Company Fields */}
                {sellerType === 'company' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        {t('seller.register.companyInfo')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('seller.register.companyName')} *</Label>
                          <Input
                            value={form.companyName}
                            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller.register.companyTaxNumber')} *</Label>
                          <Input
                            value={form.companyTaxNumber}
                            onChange={(e) => setForm({ ...form, companyTaxNumber: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('seller.register.tradeRegisterNumber')} *</Label>
                          <Input
                            value={form.companyTradeRegister}
                            onChange={(e) => setForm({ ...form, companyTradeRegister: e.target.value })}
                            placeholder={t('seller.register.tradeRegisterPlaceholder')}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('seller.register.authorizedPerson')} *</Label>
                          <Input
                            value={form.authorizedPersonName}
                            onChange={(e) => setForm({ ...form, authorizedPersonName: e.target.value })}
                            placeholder={t('seller.register.authorizedPersonPlaceholder')}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('seller.register.tradeRegisterDoc')} *</Label>
                          <div className={`border-2 border-dashed rounded-lg p-4 text-center ${form.tradeRegisterDocUrl ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
                            {form.tradeRegisterDocUrl ? (
                              <div className="flex items-center justify-center gap-2 text-green-600">
                                <Check className="w-5 h-5" />
                                <span className="text-sm">{t('seller.register.documentUploaded')}</span>
                              </div>
                            ) : (
                              <FileText className="w-6 h-6 mx-auto text-muted-foreground" />
                            )}
                            <input
                              ref={tradeDocInputRef}
                              type="file"
                              accept={DOC_ACCEPT}
                              onChange={handleTradeRegisterDocChange}
                              className="hidden"
                              id="trade-register-doc"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              onClick={() => tradeDocInputRef.current?.click()}
                              disabled={uploading}
                            >
                              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                              {t('seller.register.selectFile')}
                            </Button>
                            <p className="text-xs text-muted-foreground mt-3">{t('seller.register.acceptedFileHint', 'Kabul edilen dosyalar: JPG, PNG, WebP veya PDF. En fazla 10 MB.')}</p>
                            {docWarnings.trade ? (
                              <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-left" role="alert">
                                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700">{docWarnings.trade}</p>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>{t('seller.register.authorizedPersonId')} *</Label>
                          <div className={`border-2 border-dashed rounded-lg p-4 text-center ${form.authorizedPersonIdUrl ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
                            {form.authorizedPersonIdUrl ? (
                              <div className="flex items-center justify-center gap-2 text-green-600">
                                <Check className="w-5 h-5" />
                                <span className="text-sm">{t('seller.register.documentUploaded')}</span>
                              </div>
                            ) : (
                              <UserCircle className="w-6 h-6 mx-auto text-muted-foreground" />
                            )}
                            <input
                              ref={authorizedIdInputRef}
                              type="file"
                              accept={DOC_ACCEPT}
                              onChange={handleAuthorizedIdChange}
                              className="hidden"
                              id="authorized-id-doc"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              onClick={() => authorizedIdInputRef.current?.click()}
                              disabled={uploading}
                            >
                              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                              {t('seller.register.selectFile')}
                            </Button>
                            <p className="text-xs text-muted-foreground mt-3">{t('seller.register.acceptedFileHint', 'Kabul edilen dosyalar: JPG, PNG, WebP veya PDF. En fazla 10 MB.')}</p>
                            {docWarnings.auth ? (
                              <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-left" role="alert">
                                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700">{docWarnings.auth}</p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">{t('seller.register.approvalNotice')}</p>
                    <p className="text-sm text-amber-700 mt-1">{t('seller.register.approvalNoticeDesc')}</p>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setCurrentStep(2)} className="rounded-full px-8">
                    {t('common.back')}
                  </Button>
                  <Button 
                    type="submit" 
                    className="rounded-full px-8"
                    disabled={loading || uploading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        {t('seller.register.submitApplication')}
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('auth.alreadyHaveAccount')}{' '}
                  <Link to={loginPath} className="text-primary font-medium hover:underline">
                    {t('auth.signIn')}
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerRegisterPage;
